import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, CheckCircle } from 'lucide-react';
import PopoverPortal from './PopoverPortal';

export interface MultiSelectDropdownProps {
  label: string;
  options: { id: string; name: string }[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selected,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // All items: "All {label}" at index 0, then options
  const allItems = [{ id: '__all__', name: `All ${label}` }, ...options];

  const toggleOption = (id: string) => {
    if (id === '__all__') {
      onChange([]);
    } else if (selected.includes(id)) {
      onChange(selected.filter(s => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const displayText =
    selected.length === 0
      ? `All ${label}`
      : selected.length === 1
        ? options.find(o => o.id === selected[0])?.name
        : `${selected.length} selected`;

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        buttonRef.current?.focus();
        event.preventDefault();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
        } else {
          setFocusedIndex(prev => Math.min(prev + 1, allItems.length - 1));
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) {
          setFocusedIndex(prev => Math.max(prev - 1, 0));
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
        } else if (focusedIndex >= 0) {
          toggleOption(allItems[focusedIndex].id);
        }
        break;
      case 'Home':
        if (isOpen) {
          event.preventDefault();
          setFocusedIndex(0);
        }
        break;
      case 'End':
        if (isOpen) {
          event.preventDefault();
          setFocusedIndex(allItems.length - 1);
        }
        break;
      case 'Tab':
        if (isOpen) {
          setIsOpen(false);
          setFocusedIndex(-1);
        }
        break;
    }
  }, [isOpen, focusedIndex, allItems]);

  // Scroll focused item into view
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && listRef.current) {
      const focusedElement = listRef.current.children[focusedIndex] as HTMLElement;
      focusedElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex, isOpen]);

  const isItemSelected = (id: string) => {
    if (id === '__all__') return selected.length === 0;
    return selected.includes(id);
  };

  return (
    <div ref={containerRef} className="relative" onKeyDown={handleKeyDown}>
      <button
        ref={buttonRef}
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) setFocusedIndex(0);
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`${label} filter: ${displayText}`}
        className="flex items-center justify-between gap-2 px-3 py-2 bg-ui-background-secondary border border-ui-border-primary rounded-lg text-sm text-ui-text-primary hover:border-brand-primary/50 transition-colors"
      >
        <span>{displayText}</span>
        <ChevronDown
          className={`w-4 h-4 text-ui-text-tertiary transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <PopoverPortal
        anchorRef={containerRef}
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
          setFocusedIndex(-1);
        }}
        width={224}
        innerRef={listRef}
        className="bg-ui-background-secondary border border-ui-border-primary rounded-lg shadow-lg max-h-64 overflow-y-auto"
      >
        <div
          role="listbox"
          aria-label={`${label} options`}
          aria-multiselectable="true"
          className="p-2 space-y-1"
        >
            {allItems.map((item, index) => (
              <button
                key={item.id}
                role="option"
                aria-selected={isItemSelected(item.id)}
                onClick={() => toggleOption(item.id)}
                className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${
                  isItemSelected(item.id)
                    ? 'bg-brand-primary/10 text-brand-primary'
                    : 'text-ui-text-primary hover:bg-ui-background-tertiary'
                } ${focusedIndex === index ? 'ring-2 ring-brand-primary ring-inset' : ''}`}
              >
                {item.id !== '__all__' && (
                  <div
                    className={`w-4 h-4 rounded border ${
                      isItemSelected(item.id)
                        ? 'bg-brand-primary border-brand-primary'
                        : 'border-ui-border-primary'
                    } flex items-center justify-center`}
                  >
                    {isItemSelected(item.id) && <CheckCircle className="w-3 h-3 text-white" />}
                  </div>
                )}
                {item.name}
              </button>
            ))}
        </div>
      </PopoverPortal>
    </div>
  );
};

export default MultiSelectDropdown;
